import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import { useGetPAPart, useExportPAPart } from "@/hooks/PAHooks";
import { useGetReasonPriceChange } from "@/hooks/masterHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { usePermissions } from "@/hooks/usePermissions";
import type { PAPart as PaPartType } from "@/api/PAService";
import { exportPAPartToExcel } from "@/utils/excel/exportPAPart";
import {
  formattedDate,
  formattedDateTime,
} from "@/utils/date";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
} from "@/styles/globalstyles";

const today = new Date().toISOString().split("T")[0];

const validateDates = (from: string, to: string) => {
  if (from && from > today)
    return { fromError: "ไม่สามารถเลือกเกินวันปัจจุบัน", toError: "" };
  if (to && to > today)
    return { fromError: "", toError: "ไม่สามารถเลือกเกินวันปัจจุบัน" };
  if (from && to && from > to)
    return { fromError: "วันเริ่มต้นต้องไม่เกินวันสิ้นสุด", toError: "" };
  return { fromError: "", toError: "" };
};

const PAPart = () => {
  const { canView } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // Set defaults on first load
  useEffect(() => {
    const needsUpdate =
      !searchParams.get("result") ||
      !searchParams.get("date_from") ||
      !searchParams.get("date_to");
    if (!needsUpdate) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!next.get("result")) next.set("result", "Wait");
        if (!next.get("date_from")) {
          const from = new Date();
          from.setMonth(from.getMonth() - 1);
          next.set("date_from", from.toISOString().split("T")[0]);
        }
        if (!next.get("date_to")) {
          next.set("date_to", today);
        }
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page = useMemo(
    () => Number(searchParams.get("page")) || 0,
    [searchParams],
  );
  const rowsPerPage = useMemo(
    () => Number(searchParams.get("limit")) || 10,
    [searchParams],
  );
  const searchFilter = searchParams.get("search") || "";
  const supplierFilter = searchParams.get("supplier") || "";
  const result = (searchParams.get("result") || "") as
    | ""
    | "Wait"
    | "success";
  const reasonId = searchParams.get("reason_id")
    ? Number(searchParams.get("reason_id"))
    : undefined;
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";

  // Draft filter state — only applied to the URL (and thus the query) when
  // the user clicks "ค้นหา" (or presses Enter), instead of on every keystroke.
  const [draftSearch, setDraftSearch] = useState(searchFilter);
  const [draftSupplier, setDraftSupplier] = useState(supplierFilter);
  const [draftResult, setDraftResult] = useState(result);
  const [draftReasonId, setDraftReasonId] = useState(reasonId);
  const [draftDateFrom, setDraftDateFrom] = useState(dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(dateTo);

  useEffect(() => {
    setDraftSearch(searchFilter);
    setDraftSupplier(supplierFilter);
    setDraftResult(result);
    setDraftReasonId(reasonId);
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
  }, [searchFilter, supplierFilter, result, reasonId, dateFrom, dateTo]);

  const { fromError, toError } = useMemo(
    () => validateDates(draftDateFrom, draftDateTo),
    [draftDateFrom, draftDateTo],
  );
  const hasDateError = !!fromError || !!toError;

  // Bumped on every "ค้นหา" click so identical filters still force a refetch.
  const [searchTrigger, setSearchTrigger] = useState(0);

  const { data, isFetching, isError, error, refetch } = useGetPAPart({
    search_filter: searchFilter || undefined,
    supplier_filter: supplierFilter || undefined,
    result: result || undefined,
    reason_id: reasonId,
    create_date_from: dateFrom || undefined,
    create_date_to: dateTo || undefined,
    skip: page * rowsPerPage,
    limit: rowsPerPage,
  });

  useEffect(() => {
    if (searchTrigger > 0) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTrigger]);

  const exportMutation = useExportPAPart();

  const { data: reasonOptions } = useGetReasonPriceChange(
    { active_only: true },
    true,
  );

  useQueryHandler({
    isError,
    error,
    errorTitle: "โหลดข้อมูลไม่สำเร็จ",
  });

  useQueryHandler({
    isFetching: exportMutation.isPending,
    isError: exportMutation.isError,
    error: exportMutation.error,
    errorTitle: "Export ไม่สำเร็จ",
  });

  useEffect(() => {
    document.title = "PA Part | AMPA";
  }, []);

  const handleSearch = () => {
    if (hasDateError) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "0");
      if (draftSearch) next.set("search", draftSearch);
      else next.delete("search");
      if (draftSupplier) next.set("supplier", draftSupplier);
      else next.delete("supplier");
      if (draftResult) next.set("result", draftResult);
      else next.delete("result");
      if (draftReasonId) next.set("reason_id", String(draftReasonId));
      else next.delete("reason_id");
      if (draftDateFrom) next.set("date_from", draftDateFrom);
      else next.delete("date_from");
      if (draftDateTo) next.set("date_to", draftDateTo);
      else next.delete("date_to");
      return next;
    }, { replace: true });
    setSearchTrigger((c) => c + 1);
  };

  const handleExport = () => {
    exportMutation.mutate(
      {
        search_filter: searchFilter || undefined,
        supplier_filter: supplierFilter || undefined,
        result: result || undefined,
        reason_id: reasonId,
        create_date_from: dateFrom || undefined,
        create_date_to: dateTo || undefined,
      },
      {
        onSuccess: (res) => exportPAPartToExcel(res.data),
      },
    );
  };

  const columns: GridColDef<PaPartType>[] = [
    {
      field: "pa_part_id",
      headerName: "PA Part ID",
      width: 100,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "pa_supplier_id",
      headerName: "PA Supplier ID",
      width: 120,
      headerAlign: "right",
      align: "right",
    },
    { field: "supplier_code", headerName: "Supplier Code", width: 130 },
    { field: "part_no", headerName: "Part No", width: 150 },
    { field: "part_name", headerName: "Part Name", width: 200 },
    {
      field: "ac",
      headerName: "AC",
      width: 80,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "routing",
      headerName: "Routing",
      width: 90,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "production_proposed",
      headerName: "Production",
      width: 110,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "service_proposed",
      headerName: "Service",
      width: 90,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "tooling_proposed",
      headerName: "Tooling",
      width: 90,
      headerAlign: "right",
      align: "right",
    },
    { field: "pa_no", headerName: "PA No", width: 120 },
    { field: "confirmation_no", headerName: "Confirmation No", width: 140 },
    {
      field: "result",
      headerName: "Result",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "part_effective_date",
      headerName: "Effective Date",
      width: 130,
      valueFormatter: (v) => (v ? formattedDate(new Date(v as string)) : "-"),
    },
    { field: "reason", headerName: "Reason", width: 150 },
    { field: "note", headerName: "Note", width: 150 },
    {
      field: "create_date",
      headerName: "Created Date",
      width: 150,
      valueFormatter: (v) => (v ? formattedDateTime(v as string) : "-"),
    },
    {
      field: "update_date",
      headerName: "Updated Date",
      width: 150,
      valueFormatter: (v) => (v ? formattedDateTime(v as string) : "-"),
    },
  ];

  if (!canView("pa.part")) return null;

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Breadcrumb current="pa.part" />
      <Box
        sx={{
          mt: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          backgroundColor: "#fafafa",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <TextField
            label="ค้นหา (Part No, Part Name, PA Part ID)"
            size="small"
            value={draftSearch}
            onChange={(e) => setDraftSearch(e.target.value)}
            sx={{
              width: 300,
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#cccccc",
                },
                "&:hover fieldset": {
                  borderColor: "#999999",
                },
              },
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <TextField
            label="ค้นหา (Supplier ID, Supplier Code)"
            size="small"
            value={draftSupplier}
            onChange={(e) => setDraftSupplier(e.target.value)}
            sx={{
              width: 300,
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#cccccc",
                },
                "&:hover fieldset": {
                  borderColor: "#999999",
                },
              },
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <FormControl size="small" sx={{ width: 140 }}>
            <InputLabel
              sx={{
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              }}
            >
              Result
            </InputLabel>
            <Select
              value={draftResult}
              label="Result"
              onChange={(e) =>
                setDraftResult(e.target.value as "" | "Wait" | "success")
              }
            >
              <MenuItem value="">ทั้งหมด</MenuItem>
              <MenuItem value="Wait">Wait</MenuItem>
              <MenuItem value="success">success</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel
              sx={{
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              }}
            >
              Reason
            </InputLabel>
            <Select
              value={draftReasonId ?? ""}
              label="Reason"
              onChange={(e) =>
                setDraftReasonId(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
            >
              <MenuItem value="">ทั้งหมด</MenuItem>
              {[...(reasonOptions ?? [])].sort((a, b) => a.id - b.id).map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <TextField
            label="Created Date From"
            type="date"
            size="small"
            value={draftDateFrom}
            onChange={(e) => setDraftDateFrom(e.target.value)}
            sx={{
              width: 175,
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#cccccc",
                },
                "&:hover fieldset": {
                  borderColor: "#999999",
                },
              },
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { max: today },
            }}
            error={!!fromError}
            helperText={fromError}
          />
          <TextField
            label="Created Date To"
            type="date"
            size="small"
            value={draftDateTo}
            onChange={(e) => setDraftDateTo(e.target.value)}
            sx={{
              width: 175,
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#cccccc",
                },
                "&:hover fieldset": {
                  borderColor: "#999999",
                },
              },
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { max: today },
            }}
            error={!!toError}
            helperText={toError}
          />
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              sx={{ ...DefaultSecondaryButton }}
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              Export
            </Button>
            <Button
              size="small"
              startIcon={<SearchIcon />}
              sx={{ ...DefaultPrimaryButton }}
              onClick={handleSearch}
            >
              ค้นหา
            </Button>
          </Box>
        </Box>

        <DataGrid
          rows={data?.data ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          getRowId={(row) => row.pa_part_id}
          paginationMode="server"
          paginationModel={{ page, pageSize: rowsPerPage }}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          disableColumnSelector
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
          sx={{ minHeight: "50vh", maxHeight: "70vh" }}
          onPaginationModelChange={(model) => {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("page", String(model.page));
              next.set("limit", String(model.pageSize));
              return next;
            }, { replace: true });
          }}
        />
      </Box>
    </Box>
  );
};

export default PAPart;
